// Push Notification Service for RecepPet
// Handles browser push notifications using the Web Push API

const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

class PushNotificationService {
    constructor() {
        this.swRegistration = null;
        this.isSubscribed = false;
        this.permission = this._getNotificationPermission();
    }

    // Safe access to Notification.permission
    _getNotificationPermission() {
        try {
            return 'Notification' in window ? Notification.permission : 'denied';
        } catch (e) {
            return 'denied';
        }
    }

    // Check if push notifications are supported
    isSupported() {
        return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }

    // Initialize the service worker and push manager
    async init() {
        if (!this.isSupported()) {
            return false;
        }

        try {
            // Register service worker
            this.swRegistration = await navigator.serviceWorker.register('/sw.js');

            // Check current subscription
            const subscription = await this.swRegistration.pushManager.getSubscription();
            this.isSubscribed = subscription !== null;

            return true;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
            return false;
        }
    }

    // Request notification permission
    async requestPermission() {
        if (!this.isSupported()) {
            return 'unsupported';
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission;
        } catch (error) {
            console.error('Permission request failed:', error);
            return 'denied';
        }
    }

    // Subscribe to push notifications
    async subscribe() {
        if (!this.swRegistration) {
            await this.init();
        }

        if (this.permission !== 'granted') {
            const permission = await this.requestPermission();
            if (permission !== 'granted') {
                return null;
            }
        }

        try {
            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });

            this.isSubscribed = true;
            
            // Save subscription to localStorage for reference
            try {
                localStorage.setItem('receppet_push_subscription', JSON.stringify(subscription));
                localStorage.setItem('receppet_push_enabled', 'true');
            } catch (e) {
                console.warn('Could not save to localStorage');
            }

            return subscription;
        } catch (error) {
            console.error('Failed to subscribe:', error);
            return null;
        }
    }

    // Unsubscribe from push notifications
    async unsubscribe() {
        if (!this.swRegistration) {
            return true;
        }

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
            }
            
            this.isSubscribed = false;
            try {
                localStorage.removeItem('receppet_push_subscription');
                localStorage.setItem('receppet_push_enabled', 'false');
            } catch (e) {
                console.warn('Could not access localStorage');
            }
            
            return true;
        } catch (error) {
            console.error('Failed to unsubscribe:', error);
            return false;
        }
    }

    // Show a local notification (for immediate display without server push)
    async showLocalNotification(title, options = {}) {
        if (this.permission !== 'granted') {
            return false;
        }

        const defaultOptions = {
            icon: '/logo192.png',
            badge: '/logo192.png',
            vibrate: [100, 50, 100],
            requireInteraction: false,
            silent: false
        };

        try {
            if (this.swRegistration) {
                await this.swRegistration.showNotification(title, { ...defaultOptions, ...options });
            } else {
                new Notification(title, { ...defaultOptions, ...options });
            }
            return true;
        } catch (error) {
            console.error('Failed to show notification:', error);
            return false;
        }
    }

    // Check if user has enabled push notifications
    isPushEnabled() {
        try {
            return localStorage.getItem('receppet_push_enabled') === 'true';
        } catch (e) {
            return false;
        }
    }

    // Helper to convert VAPID key
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    // Get current permission status
    getPermissionStatus() {
        if (!this.isSupported()) return 'unsupported';
        return this._getNotificationPermission();
    }
}

// Singleton instance
const pushService = new PushNotificationService();

export default pushService;
